import { Form, Formik } from "formik";
import { observable } from "mobx";
import { inject, observer } from "mobx-react";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import React, { Component } from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { compose } from "recompose";
import * as yup from "yup";

import { CheckboxField } from "nvent-web/components/form/CheckboxField";
import CountriesField from "nvent-web/components/form/CountriesField";
import { TextField } from "nvent-web/components/form/TextField";
import { buildMessage } from "nvent-web/i18n/yup";

import { CompanyProps, RegistrationFlow } from "../../stores/RegistrationFlow";
import { RequiredFieldLabel, SubmitButton } from "./common";
import style from "./CompanyForm.module.scss";

export interface CompanyFormInnerProps extends InjectedIntlProps {
  registration: RegistrationFlow;
}

const validationSchema = yup.object({
  companyName: yup.string().required(),
  companyAddress: yup
    .object({
      streetAddress: yup.string().required(),
      postalCode: yup.string().required(),
      locality: yup.string().required(),
      countryCode: yup.string().required(),
    })
    .required(),
  consents: yup.object({
    termsOfService: yup.boolean().oneOf([true], buildMessage("error.required")),
    newsletter: yup.boolean(),
  }),
});

export class CompanyFormInner extends Component<CompanyFormInnerProps> {
  @observable
  submitPromise?: IPromiseBasedObservable<void>;

  render() {
    const { registration } = this.props;
    const { companyName, companyAddress, consents } = registration;
    const initialValues: CompanyProps = {
      companyName,
      companyAddress,
      consents,
    };
    const isSubmitting = this.submitPromise && this.submitPromise.state === "pending";

    const termsOfServiceLabel = (
      <FormattedMessage
        id="form.consents.termsOfService"
        values={{
          link: (
            <a target="_blank" rel="noreferrer noopener" href="https://www.nvent.com/us/en/terms-of-use.html">
              <FormattedMessage id="form.consents.termsOfService.link" />
            </a>
          ),
        }}
      />
    );

    return (
      <Formik initialValues={initialValues} onSubmit={this.submit} validationSchema={validationSchema}>
        {() => (
          <Form noValidate={true}>
            <TextField name="companyName" label={<FormattedMessage id="form.companyName" />} required />

            <TextField
              name="companyAddress.streetAddress"
              label={<FormattedMessage id="form.address.streetAddress" />}
              required
            />

            <div className={style.row}>
              <div className={style.shortField}>
                <TextField
                  name="companyAddress.postalCode"
                  label={<FormattedMessage id="form.address.postalCode" />}
                  required
                />
              </div>

              <div className={style.shortField}>
                <TextField
                  name="companyAddress.locality"
                  label={<FormattedMessage id="form.address.locality" />}
                  required
                />
              </div>
            </div>

            <CountriesField
              name="companyAddress.countryCode"
              label={<FormattedMessage id="form.address.country" />}
              required
            />

            <CheckboxField name="consents.termsOfService" label={termsOfServiceLabel} required />
            <CheckboxField
              name="consents.newsletter"
              label={<FormattedMessage id="form.consents.newsletter" values={{}} />}
            />

            <RequiredFieldLabel />
            <SubmitButton disabled={isSubmitting} loading={isSubmitting}>
              <FormattedMessage id="register.submit" />
            </SubmitButton>
          </Form>
        )}
      </Formik>
    );
  }

  private submit = (company: CompanyProps) => {
    this.submitPromise = fromPromise(this.props.registration.companySubmitted(company));
  };
}

export const CompanyForm = compose<CompanyFormInnerProps, {}>(
  inject("registration"),
  injectIntl,
  observer
)(CompanyFormInner);

export default CompanyForm;
